import { useCallback, useEffect, useState } from "react";

import { endpoints } from "@/configs/Settings";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { GetPrescriptionProtocol } from "@/services/fhir";

import { buildQuestionnaireToProtocolNamesMap } from "../buildProtocolQuestionnaireIndex";
import { normalizeQuestionnaireLogicalIdForComparison } from "src-next/modules/patient-summary/transformers/questionnaireResponseExclusions";

export type ProtocolQuestionnaireIndexStatus = "idle" | "loading" | "error" | "success";

export interface UseOrganizationProtocolQuestionnaireIndexOptions {
  /**
   * Quando falso, não dispara fetch (ex.: diálogo fechado).
   * Uma única requisição por abertura / troca de organização.
   */
  enabled: boolean;
}

export interface UseOrganizationProtocolQuestionnaireIndexResult {
  /** Mapa: id de questionário normalizado → nomes de protocolos. */
  questionnaireToProtocolNames: Map<string, string[]>;
  status: ProtocolQuestionnaireIndexStatus;
  /**
   * Lista de nomes de protocolos que incluem o questionário (ids comparados com `normalizeQuestionnaireLogicalIdForComparison`).
   */
  getProtocolNamesForQuestionnaire: (questionnaireRefOrId: string | undefined | null) => string[];
}

/**
 * Carrega todos os protocolos da organização (QuestionnaireResponse do template `PROTOCOL_ID`)
 * e monta índice inverso questionário → protocolos, para exibição em listas sem N+1.
 */
export function useOrganizationProtocolQuestionnaireIndex(
  options: UseOrganizationProtocolQuestionnaireIndexOptions,
): UseOrganizationProtocolQuestionnaireIndexResult {
  const { enabled } = options;
  const { organization } = useOrganizationContext();

  const [questionnaireToProtocolNames, setQuestionnaireToProtocolNames] = useState<
    Map<string, string[]>
  >(() => new Map());
  const [status, setStatus] = useState<ProtocolQuestionnaireIndexStatus>("idle");

  useEffect(() => {
    if (!enabled) {
      setQuestionnaireToProtocolNames(new Map());
      setStatus("idle");
      return;
    }

    // Sem Organization/id FHIR não dá para usar o mesmo author que `ManagerProtocol`;
    // evitamos `idle` aqui para não deixar a UI em "carregando" para sempre.
    if (!organization?.id) {
      setQuestionnaireToProtocolNames(new Map());
      setStatus("success");
      return;
    }

    let cancelled = false;

    (async () => {
      setStatus("loading");
      try {
        const response = await GetPrescriptionProtocol({
          questionnaire: endpoints.PROTOCOL_ID,
          author: organization.id,
        });
        if (cancelled) return;
        const map = buildQuestionnaireToProtocolNamesMap(response?.entry);
        setQuestionnaireToProtocolNames(map);
        setStatus("success");
      } catch (e) {
        console.error("useOrganizationProtocolQuestionnaireIndex", e);
        if (!cancelled) {
          setQuestionnaireToProtocolNames(new Map());
          setStatus("error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, organization?.id]);

  const getProtocolNamesForQuestionnaire = useCallback(
    (questionnaireRefOrId: string | undefined | null) => {
      const key = normalizeQuestionnaireLogicalIdForComparison(questionnaireRefOrId);
      if (!key) return [];
      return questionnaireToProtocolNames.get(key) ?? [];
    },
    [questionnaireToProtocolNames],
  );

  return {
    questionnaireToProtocolNames,
    status,
    getProtocolNamesForQuestionnaire,
  };
}
